import { applyAmountToSchema, payOccurrenceBodySchema } from '@klopotun/contracts';
import { z } from 'zod';
import type { ConfirmPaymentValues } from '../model/confirm-payment-values.js';
import { toPayBodyDraft } from './confirm-payment-adapter.js';

type Complaint = {
  field: keyof ConfirmPaymentValues;
  message: string;
};

const COMPLAINT_BY_BODY_FIELD: Record<string, Complaint> = {
  paidOn: { field: 'paidOn', message: 'Вкажіть дату оплати' },
  // Also the message for text that could not be read as a number at all: a thousands separator,
  // a second decimal point, a space copied out of "20 000,00" on the screen above. Naming only
  // zero would send a person looking for a mistake that is not in the number they typed.
  paidAmountMinor: {
    field: 'amount',
    message: 'Вкажіть суму — число більше нуля, без пробілів, наприклад 20000.00',
  },
  paidCurrency: { field: 'amount', message: 'Валюта періоду не підходить' },
  applyAmountTo: { field: 'applyAmountTo', message: 'Оберіть, як застосувати суму' },
};

const FALLBACK_COMPLAINT: Complaint = {
  field: 'amount',
  message: 'Дані форми не пройшли перевірку',
};

const complaintFor = (path: PropertyKey[]): Complaint => {
  const [firstSegment] = path;

  if (typeof firstSegment !== 'string') {
    return FALLBACK_COMPLAINT;
  }

  return COMPLAINT_BY_BODY_FIELD[firstSegment] ?? FALLBACK_COMPLAINT;
};

const confirmPaymentValuesSchema = z.object({
  amount: z.string(),
  paidOn: z.string(),
  applyAmountTo: applyAmountToSchema,
  currency: z.string(),
});

const checkAgainstContract = (values: ConfirmPaymentValues, ctx: z.RefinementCtx): void => {
  const checked = payOccurrenceBodySchema.safeParse(toPayBodyDraft(values));

  if (checked.success) {
    return;
  }

  for (const issue of checked.error.issues) {
    const complaint = complaintFor(issue.path);

    ctx.addIssue({ code: 'custom', path: [complaint.field], message: complaint.message });
  }
};

export const confirmPaymentSchema = confirmPaymentValuesSchema.superRefine(checkAgainstContract);
